import { BackToTop } from 'components/common';
import {
  AcquireNewCustomers,
  LifecycleJourney,
  StoreMetrics,
} from 'components/dashboard';
import CampaignRevenueAttribution from 'components/dashboard/CampaignRevenueAttribution';
import { StoreMetricsProvider } from 'components/dashboard/StoreMetrics';
import { ArrowUpIcon, SuggestionIcon } from 'components/icons';
import { formatDistanceToNow, parseISO } from 'date-fns';
import { useEffect } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { useAppSelector } from 'store';
import {
  useGetDashboardLifecycleQuery,
  useGetUserInfoQuery,
} from 'store/services/segmentsApi';
import { userDetailsState } from 'store/userDetailsSlice';
import { usePageTitle } from 'utils';
import { greetingMessage } from 'utils/helpers';
import { useQuery } from 'utils/useQuery';
import { useStoreSubscriptionPlan } from 'utils/useStoreSubscriptionPlan';
import {
  OnboardingStatus,
  useUserOnboardingStatus,
} from 'utils/useUserOnboardingStatus';

export default function DashboardPage() {
  usePageTitle('Dashboard | Segments');

  const { loggedInUser, currentStore, role_id } =
    useAppSelector(userDetailsState);

  const userOnboard = useUserOnboardingStatus();

  const { data: completeUserInfo } = useGetUserInfoQuery();

  const isUserTreslAdmin = role_id === 1;

  const history = useHistory();
  const query = useQuery();

  const isFromWelcomePage = query.get('welcome') === 'true';

  useEffect(() => {
    if (isFromWelcomePage) {
      if ((window as any)?.Intercom) {
        (window as any).Intercom('startTour', 296337);
      }
    }
  }, [isFromWelcomePage]);

  useEffect(() => {
    if (isUserTreslAdmin) return;

    const hasUserOnboarded = completeUserInfo?.has_objectives;

    if (
      (completeUserInfo && !hasUserOnboarded) ||
      userOnboard.status === OnboardingStatus.IN_PROGRESS
    ) {
      history.replace({
        pathname: '/dashboard/welcome',
        search: currentStore.serializedStoreName,
      });
    }
  }, [
    completeUserInfo,
    currentStore.serializedStoreName,
    history,
    isUserTreslAdmin,
    userOnboard.status,
  ]);

  const { data: lifecycleData } = useGetDashboardLifecycleQuery(
    { serializedStoreName: currentStore.serializedStoreName },
    {
      skip: !currentStore.serializedStoreName,
    }
  );
  const lifecycle = lifecycleData?.lifecycle;

  const dataLastUpdated = lifecycle?.last_updated
    ? formatDistanceToNow(parseISO(lifecycle?.last_updated), {
        addSuffix: true,
      })
    : '...';

  const { storeSubscriptionPlan, subscriptionPlans } =
    useStoreSubscriptionPlan();

  return (
    <div className="relative max-w-screen-xl px-5 mx-auto md:px-14 py-11">
      <p className="font-bold leading-6">Dashboard</p>

      <h1 className="mt-4 text-4xl font-bold leading-10">
        {greetingMessage()}, {loggedInUser?.split(' ')[0]}
      </h1>

      <p className="mt-4 text-base">
        The following modules are ready for you to review.
      </p>
      <div className="flex items-center space-x-1 text-segments-13px">
        <p className="text-black text-segments-13px text-opacity-30">
          Data last updated {dataLastUpdated}
        </p>
        {storeSubscriptionPlan < subscriptionPlans.small_business && (
          <Link
            to={{
              pathname: '/account/change-plan',
              search: currentStore.serializedStoreName,
            }}
            className="flex items-center font-bold text-segments-accent-color-orange"
          >
            - Upgrade to refresh your data
            <ArrowUpIcon className="w-4 h-4 text-segments-accent-color-orange" />
          </Link>
        )}
      </div>

      <button
        onClick={handleTour}
        className="inline-flex items-end mt-2 space-x-1 font-bold text-segments-link"
      >
        <p>Take a tour</p>
        <SuggestionIcon className="w-5 h-5" />
      </button>

      <StoreMetricsProvider>
        <StoreMetrics />
      </StoreMetricsProvider>

      <LifecycleJourney />
      {/* <div className="grid gap-8 md:grid-cols-2 mt-7 md:mt-16"> */}
      <div className="mt-7 md:mt-16">
        {/* <AnalyzeEmails /> */}
        <AcquireNewCustomers />
        <CampaignRevenueAttribution />
      </div>
      <BackToTop />
    </div>
  );

  function handleTour() {
    if ((window as any)?.Intercom) {
      (window as any).Intercom('startTour', 296337);
    }
  }
}
